import React, { useState } from 'react';
import {
  canonicalizeAmount,
  formatAmount,
  stripTrailingZeros,
} from '../../utils/amountFormat';

// Controlled text input for amounts (money, tonnage, quantities, counts).
//
// Replaces <input type="number">, which (a) cannot display thousands
// separators at all, (b) lets the browser locale render decimals with a
// comma — backend "300.000" shows as "300,000" on German-locale machines —
// and (c) lets Safari pass arbitrary text through (same reason the
// commission field in FuelEnquiryForm uses a guarded text input).
//
// Contract:
//   - ``value``: canonical dot-decimal string, number, or ''/null. Backend
//     DecimalField strings ("150.00", "801.0000") are accepted directly.
//   - ``onChange(canonical)``: fired per keystroke with the canonical
//     dot-decimal string ('' when cleared) — safe to store in form state and
//     submit to DRF unchanged.
//   - ``kind``: 'decimal' (default; decimals appear only when typed) or
//     'integer' (digits only — credit days, counts; no grouping ambiguity).
//   - ``maxDecimals``: cap typed decimal places to match the backend
//     DecimalField scale (e.g. 2) so a save can never 400 on
//     "no more than N decimal places". Omit for no cap.
//   - ``allowNegative``: permit a leading '-' (e.g. funding-days override,
//     which the backend documents as legitimately negative). Off by default.
//   - ``onBlur``: chained after the internal blur reformatting.
//   - Any other prop (data-* attributes, style, autoFocus, required, …)
//     passes through to the underlying <input>.
//
// While focused, the trader edits raw ungrouped text ("1250.5"); on blur it
// renders grouped with phantom trailing zeros stripped ("1,250.5").
const buildGuard = (kind, maxDecimals, allowNegative) => {
  const sign = allowNegative ? '-?' : '';
  if (kind === 'integer') return new RegExp(`^${sign}\\d*$`);
  const frac = maxDecimals != null ? `\\d{0,${maxDecimals}}` : '\\d*';
  return new RegExp(`^${sign}\\d*\\.?${frac}$`);
};

const AmountInput = ({
  value,
  onChange,
  kind = 'decimal',
  maxDecimals = null,
  allowNegative = false,
  disabled = false,
  placeholder,
  name,
  id,
  className,
  onBlur,
  ...rest
}) => {
  // null = not editing (show formatted value); string = live draft text.
  const [draft, setDraft] = useState(null);
  const guard = buildGuard(kind, maxDecimals, allowNegative);

  const handleFocus = () => {
    setDraft(stripTrailingZeros(canonicalizeAmount(value)));
  };

  // Interpret comma/dot separators in typed or pasted text. Traders here
  // work in BOTH conventions (US "1,250.50" and German "1.250,50"), and the
  // old type="number" inputs accepted German decimal commas on de-locale
  // browsers — so commas must be interpreted, never silently swallowed
  // (which would corrupt "300,5" into "3005"). Rules:
  //   - Both separators present: whichever occurs LAST is the decimal
  //     separator ("1,250.50" and "1.250,50" both -> 1250.50).
  //   - Commas only, several of them: grouping ("1,250,000" -> 1250000).
  //   - One comma with exactly 3 digits after it and nothing else: genuinely
  //     ambiguous (US grouping 300,000 vs German decimal 300,000 = 300) --
  //     returns null so the paste safe-fails as a no-op instead of guessing
  //     a 1000x-wrong value. Typing never produces this shape: a typed
  //     comma becomes a dot on that very keystroke.
  //   - One comma otherwise: German decimal habit -> dot ("300,5" -> 300.5).
  // A bare dot is ALWAYS the decimal separator (app-wide canonical form);
  // German dot-grouped pastes like "300.000" are indistinguishable from a
  // legitimate 3dp decimal and parse as 300 -- same as the old inputs.
  const interpretSeparators = (text) => {
    if (kind === 'integer' || !text.includes(',')) return text;
    const lastComma = text.lastIndexOf(',');
    const lastDot = text.lastIndexOf('.');
    if (lastDot !== -1) {
      return lastDot > lastComma
        ? text.replace(/,/g, '')
        : text.replace(/\./g, '').replace(',', '.');
    }
    if ((text.match(/,/g) || []).length > 1) return text.replace(/,/g, '');
    if (/,\d{3}$/.test(text)) return null;
    return text.replace(',', '.');
  };

  const handleChange = (e) => {
    const text = interpretSeparators(e.target.value.trim());
    if (text === null) return; // ambiguous paste: safe-fail, keep state
    if (text !== '' && !guard.test(text)) return; // reject junk keystrokes
    setDraft(text);
    if (onChange) onChange(canonicalizeAmount(text));
  };

  const handleBlur = (e) => {
    setDraft(null);
    if (onBlur) onBlur(e);
  };

  return (
    <input
      {...rest}
      type="text"
      inputMode={kind === 'integer' ? 'numeric' : 'decimal'}
      autoComplete="off"
      value={draft !== null ? draft : formatAmount(value)}
      onFocus={handleFocus}
      onChange={handleChange}
      onBlur={handleBlur}
      disabled={disabled}
      placeholder={placeholder}
      name={name}
      id={id}
      className={className}
    />
  );
};

export default AmountInput;
